import React, { FC, useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { EAssetType } from 'constants/enums';
import { Database, Info } from 'lucide-react';
import { ProjectCategory } from 'views/Projects/constants';

import { Tooltip } from 'components/Tooltip/Tooltip';

import { cn } from '../lib/utils';
import Typography from '../Typography/Typography';
import { Button } from '../ui/button';
import { Dialog, DialogOverlay, DialogTrigger } from '../ui/dialog';

import UploadImageModal from './UploadImageModal/UploadImageModal';
import { UploadImagePreview } from './UploadImagePreview';

type UploadImageProps = {
  title?: string;
  description?: string;
  buttonText?: string;
  className?: string;
  variant?: 'default' | 'storeSettings';
  onUpload?: (url: string, name: string, mimeType: string) => void;
  isRequired?: boolean;
  selectedImageUrl?: string;
  modalOnly?: boolean;
  uploadOnly?: boolean;
  isModalOpen?: boolean;
  externalModalOpenFunction?: (open: boolean) => void;
  forceType?: EAssetType;
  uploadContext?: ProjectCategory;
  hideSuccessToast?: boolean;
  'data-testid'?: string;
  buttonDataTestId?: string;
  labelClassName?: string;
  TooltipContent?: React.FC;
  hideInfoIcon?: boolean;
  hasError?: boolean;
  errorMessage?: string;
};

const storeSettingsBlockBackground =
  'linear-gradient(0deg, rgba(244, 244, 245, 0.30) 0%, rgba(244, 244, 245, 0.30) 100%), #FFF';

const storeSettingsContainerStyles =
  'flex h-[107px] min-w-[77.395px] w-full flex-[1_0_0] items-center justify-between rounded-md border border-dashed border-[rgba(161,161,170,0.2)] px-[22px] py-5';

const UploadImage: FC<UploadImageProps> = ({
  title = '',
  description = '',
  buttonText = 'Upload',
  className,
  variant = 'default',
  onUpload,
  isRequired = false,
  selectedImageUrl = '',
  modalOnly = false,
  uploadOnly = false,
  isModalOpen = false,
  externalModalOpenFunction,
  forceType,
  'data-testid': dataTestId,
  buttonDataTestId,
  labelClassName,
  TooltipContent,
  hideInfoIcon = false,
  hasError = false,
  errorMessage
}) => {
  const [isOpen, setIsOpen] = useState(isModalOpen);

  const handleUpload = (url: string, name: string, mimeType: string) => {
    onUpload?.(url, name, mimeType);
  };

  const handleRemoveImage = () => {
    onUpload?.('', '', '');
  };

  useEffect(() => {
    if (externalModalOpenFunction && isOpen !== isModalOpen) {
      externalModalOpenFunction(isOpen);
    }
  }, [isOpen, externalModalOpenFunction, isModalOpen]);

  const iconTooltipContent = TooltipContent ? (
    <TooltipContent />
  ) : description?.trim() ? (
    <span className="block max-w-xs whitespace-pre-line text-left">
      {description}
    </span>
  ) : null;

  const showIconTooltip = Boolean(iconTooltipContent);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogOverlay className="bg-alpha-80" />
      <div
        data-testid={dataTestId}
        style={{
          display: modalOnly && !isOpen ? 'none' : 'flex',
          ...(variant === 'storeSettings' && {
            background: storeSettingsBlockBackground
          })
        }}
        className={cn(
          variant === 'storeSettings'
            ? storeSettingsContainerStyles
            : 'flex w-full items-center justify-between gap-4 rounded-lg border border-dashed border-base-sidebarBorder bg-white px-6 py-4',
          variant === 'default' && selectedImageUrl && 'py-4',
          hasError && 'border-base-destructive border-solid',
          className
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {!hideInfoIcon &&
            (showIconTooltip ? (
              <Tooltip
                content={iconTooltipContent}
                className="z-[1000] shrink-0"
              >
                <button
                  type="button"
                  className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-base-mutedForeground"
                  aria-label={description?.trim() ? description : 'More info'}
                >
                  <Info size={16} className="pointer-events-none" aria-hidden />
                </button>
              </Tooltip>
            ) : (
              <Info
                size={16}
                className="shrink-0 cursor-default text-base-mutedForeground"
              />
            ))}
          <div className="min-w-0">
            <Typography
              className={cn(
                'flex items-center gap-1 font-medium text-[12px] leading-4',
                variant === 'storeSettings'
                  ? 'font-sans text-base-secondaryForeground'
                  : 'text-base-foreground',
                labelClassName
              )}
            >
              {isRequired && (
                <FontAwesomeIcon
                  icon="fa-regular fa-asterisk"
                  className="h-2 w-2 text-[8px] text-destructive"
                />
              )}
              {title}
            </Typography>
          </div>
        </div>
        <DialogTrigger asChild>
          {selectedImageUrl ? (
            <UploadImagePreview
              preview={selectedImageUrl}
              handleRemoveImage={handleRemoveImage}
            />
          ) : (
            <Button
              type="button"
              variant="secondary"
              className={cn(
                'flex h-auto min-h-0 shrink-0 cursor-pointer items-stretch gap-0 overflow-hidden rounded-md border border-solid border-base-input bg-base-border px-0 py-0 text-base-foreground shadow-none transition-opacity hover:bg-base-border hover:opacity-90',
                variant === 'storeSettings' &&
                  'h-10 min-w-[111px] gap-3.5 pl-4 pr-0'
              )}
              data-testid={buttonDataTestId}
            >
              <span
                className={cn(
                  'flex items-center text-sm font-medium text-base-foreground',
                  variant === 'storeSettings' ? '' : 'px-4 py-2'
                )}
              >
                {buttonText}
              </span>
              <div
                className="w-px shrink-0 bg-white"
                aria-hidden
              />
              <div
                className={cn(
                  'flex items-center justify-center text-base-secondaryForeground',
                  variant === 'storeSettings' ? 'pr-3' : 'px-2'
                )}
              >
                <Database size={16} />
              </div>
            </Button>
          )}
        </DialogTrigger>
        <UploadImageModal
          onClose={() => setIsOpen(false)}
          onUpload={handleUpload}
          selectedImageUrl={selectedImageUrl}
          forceType={forceType}
          uploadOnly={uploadOnly}
        />
      </div>
      {hasError && errorMessage && (
        <p className="mt-1.5 text-sm text-base-destructive">{errorMessage}</p>
      )}
    </Dialog>
  );
};

export default UploadImage;
